/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prefer-rest-params */ import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NO_INTERCEPT } from '@campus/browser';
import { Observable, of, throwError } from 'rxjs';
import { catchError, mapTo, tap } from 'rxjs/operators';
import {
  AccessTokenInterface,
  AlertQueueInterface,
  BundleInterface,
  ContextInterface,
  FavoriteInterface,
  LearningPlanGoalInterface,
  LearningPlanGoalProgressInterface,
  MethodGoalInterface,
  PassportUserCredentialInterface,
  PersonInterface,
  PersonPreferenceInterface,
  RealmInterface,
  ResultInterface,
  SchoolRoleMappingInterface,
  StudentContentStatusInterface,
  TaskInterface,
  UnlockedFreePracticeInterface,
  UserContentInterface,
  UserLessonInterface,
} from '../../+models';
import {
  LearningPlanGoalProgressWithEduContentTocInterface,
  LearningPlanGoalProgressWithUserLessonInterface,
} from '../../+state/learning-plan-goal-progress/learning-plan-goal-progress.actions';
import { DAL_OPTIONS, DalOptions } from '../../dal.module';
import { PersonDataResponseInterface } from '../../data/data.service.interface';
import { LoginCredentials, PersonalCodeCredentials, ValidateTokenResponse } from '../../persons';
import { ApiAuth } from '../internals';
import { ModelApiBase, PolpoApi } from '../shared';

@Injectable({ providedIn: 'root' })
export class PersonApi extends ModelApiBase {
  private dalOptions = inject<DalOptions>(DAL_OPTIONS);

  constructor(
    api: PolpoApi,
    private authApi: ApiAuth,
    private httpClient: HttpClient
  ) {
    super(api, 'People');
  }

  private baseUrl = this.dalOptions.apiBaseUrl + '/api/';

  public getData(personId: number, fields?: string | string[], since?: number | Date) {
    const path = `${personId}/data`;

    if (Array.isArray(fields)) fields = fields.join(',');
    if (since instanceof Date) since = +since;

    const params = this._getHttpParams({
      fields,
      since,
    });

    return this.get<PersonDataResponseInterface>(path, params);
  }

  public setAlertRead(personId: number, alertId: number | number[], read?: boolean, intended?: boolean) {
    const path = `${personId}/set-alert-read`;

    const params = this._getHttpParams({
      alertId,
      read,
      intended,
    });

    return this.put<PersonDataResponseInterface>(path, params);
  }

  public destroyByIdAlertQueues(personId: number, alertQueueId: number) {
    const path = `${personId}/alertQueues/${alertQueueId}`;

    return this.delete<{ count: number }>(path);
  }

  public createBundles(personId: number, bundle: BundleInterface) {
    const path = `${personId}/bundles`;

    const body = bundle;
    return this.post(path, undefined, body);
  }

  public shareBundle(personId: number, bundleId: number, teacherIds: number[], message?: string) {
    const path = `${personId}/share-bundle/${bundleId}`;

    const body = { teacherIds, message };
    return this.post(path, undefined, body);
  }

  public createUserContents(personId: number, userContent: UserContentInterface) {
    const path = `${personId}/userContents`;

    const body = userContent;
    return this.post(path, undefined, body);
  }

  public getFavorites(personId: number) {
    const path = `${personId}/favorites`;

    return this.get<FavoriteInterface[]>(path);
  }

  public createFavorites(personId: number, favorite: FavoriteInterface) {
    const path = `${personId}/favorites`;

    const body = favorite;
    return this.post<FavoriteInterface>(path, undefined, body);
  }

  public updateByIdFavorites(personId: number, favoriteId: number, changes) {
    const path = `${personId}/favorites/${favoriteId}`;

    const body = changes;
    return this.patch<FavoriteInterface>(path, undefined, body);
  }

  public destroyByIdFavorites(personId: number, favoriteId: number) {
    const path = `${personId}/favorites/${favoriteId}`;

    return this.delete<{ count: number }>(path);
  }

  public destroyByIdHistory(personId: number, historyId: number) {
    const path = `${personId}/history/${historyId}`;

    return this.delete<{ count: number }>(path);
  }

  public getLearningPlanGoalsForBookRemote(personId: number, bookId: number) {
    const path = `${personId}/learningPlanGoalsForBook/${bookId}`;

    return this.get<LearningPlanGoalInterface[]>(path);
  }

  public destroyByIdLearningPlanProgress(personId: number, learningPlanGoalProgressId: number) {
    const path = `${personId}/learningPlanProgress/${learningPlanGoalProgressId}`;

    return this.delete<{ count: number }>(path);
  }

  public destroyManyLearningPlanGoalProgress(personId: number, learningPlanGoalProgressIds: number[]) {
    const path = `${personId}/destroyManyLearningPlanGoalProgress`;

    const params = this._getHttpParams({
      ids: learningPlanGoalProgressIds,
    });

    return this.delete<{ count: number }>(path, params);
  }

  public createLearningPlanGoalProgress(
    personId: number,
    classGroupId: number,
    learningPlanGoalIds: number[],
    eduContentBookId: number,
    userLessonId?: number,
    eduContentTOCId?: number
  ) {
    const path = `${personId}/createLearningPlanGoalProgress`;

    const params = this._getHttpParams({
      classGroupId,
      learningPlanGoalIds,
      userLessonId: userLessonId || undefined,
      eduContentTOCId: eduContentTOCId || undefined,
      eduContentBookId,
    });

    return this.post<LearningPlanGoalProgressInterface[]>(path, params);
  }

  public createLearningPlanGoalProgresses(
    personId: number,
    learningGoalPlanProgresses: (
      | LearningPlanGoalProgressWithEduContentTocInterface
      | LearningPlanGoalProgressWithUserLessonInterface
    )[]
  ) {
    const path = `${personId}/createLearningPlanGoalProgresses`;

    // TODO doublecheck this
    // change to body ?
    const params = this._getHttpParams({
      progresses: learningGoalPlanProgresses as any,
    });

    return this.post<LearningPlanGoalProgressInterface[]>(path, params);
  }

  public getMethodGoalsForBookRemote(personId: number, bookId: number) {
    const path = `${personId}/getMethodGoalsForBook/${bookId}`;

    // TODO: really, a POST to *get* methodgoals?
    return this.post<MethodGoalInterface[]>(path);
  }

  public login(credentials: Partial<LoginCredentials>, include = 'user', rememberMe = true) {
    const path = `login`;

    const params = this._getHttpParams({
      include,
    });

    const body = { ...credentials };
    return this.post<any>(path, params, body).pipe(
      tap((response) => {
        response.ttl = parseInt(response.ttl, 10);
        response.rememberMe = rememberMe;
        this.authApi.setToken(response);
        return response;
      })
    );
  }

  public loginWithPersonalCode(credentials: PersonalCodeCredentials) {
    const path = `personal-code-login`;

    const body = credentials;
    return this.post(path, undefined, body);
  }

  public logout() {
    const path = `logout`;

    return this.post(path, undefined, undefined, {
      withCredentials: true,
      context: new HttpContext().set(NO_INTERCEPT, true),
    }).pipe(
      tap(() => this.authApi.setUser(null)),
      mapTo(true),
      catchError((_) => of(true))
    );
  }

  public validateToken(personId: number, tokenId: string) {
    const path = `validate-token/${personId}/${tokenId}`;

    return this.post<ValidateTokenResponse>(path);
  }

  public destroyByIdCredentials(personId: number, credential: number) {
    const path = `${personId}/credentials/${credential}`;

    return this.delete<{ count: number }>(path);
  }

  public useAvatarFromCredential(personId: number, credential: number) {
    const path = `${personId}/avatarcredential/${credential}`;

    return this.get(path);
  }

  public linkStudentToTeacherRemote(publicKey: string) {
    const path = `link-teachers`;

    const params = this._getHttpParams({
      publicKey,
    });

    return this.put<PersonInterface[]>(path, params);
  }

  public destroyByIdTeacherStudentByStudent(personId: number, teacherStudentId: number) {
    const path = `${personId}/teacherStudentByStudent/${teacherStudentId}`;

    return this.delete<{ count: number }>(path);
  }

  public unlinkStudentsFromTeacherRemote(personId: number, studentIds: number[]) {
    const path = `${personId}/unlink-students-from-teacher`;

    const params = this._getHttpParams({
      studentIds,
    });

    return this.put(path, params);
  }

  public checkUnique(personId: number, property: string, value: any) {
    const path = `checkUnique`;

    const params = this._getHttpParams({
      ...(personId && { key: personId }), // Important: key is optional but must be a number
      property,
      value,
    });

    return this.post<{ unique: boolean }>(path, params);
  }

  public checkProfileComplete() {
    const path = `check-profile-complete`;

    return this.get(path);
  }

  public checkUniqueAdmin(email: string) {
    const path = `check-unique/admin`;

    const params = this._getHttpParams({
      email,
    });

    return this.post<{ unique: boolean; user?: PersonInterface }>(path, params);
  }

  public patchAttributes(personId: number, changedProps: Partial<PersonInterface> = {}) {
    const path = `${personId}`;

    const body = changedProps;
    return this.patch<Partial<PersonInterface>>(path, undefined, body);
  }

  public resultForReview(personId: number, resultId: number) {
    const path = `${personId}/review/result/${resultId}`;

    return this.get<ResultInterface>(path);
  }

  public resultForTask(personId: number, taskId: number, eduContentId: number) {
    const path = `${personId}/task/${taskId}/result/${eduContentId}`;

    return this.get<ResultInterface>(path);
  }

  public resultForUnlockedContent(personId: number, unlockedContentId: number, eduContentId: number) {
    const path = `${personId}/unlockedContent/${unlockedContentId}/result/${eduContentId}`;

    return this.get<ResultInterface>(path);
  }

  public resultForContext(personId: number, eduContentId: number, context: ContextInterface) {
    const path = `${personId}/context/result/${eduContentId}`;

    const params = this._getHttpParams({ context } as any);

    return this.get<ResultInterface>(path, params);
  }

  public linkSchools(personId: number, schoolIds: number[]) {
    const path = `${personId}/link-schools`;

    const body = schoolIds;
    return this.post<SchoolRoleMappingInterface[]>(path, undefined, body);
  }

  public destroyByIdSchoolRoleMapping(personId: number, schoolRoleMappingId: number) {
    const path = `${personId}/schoolRoleMapping/${schoolRoleMappingId}`;
    // Observable<boolean> ?
    return this.delete<{ count: number }>(path);
  }

  public getStudentContentStatuses(personId: number) {
    const path = `${personId}/studentContentStatuses`;

    return this.get<StudentContentStatusInterface[]>(path);
  }

  public createStudentContentStatuses(personId: number, studentContentStatus: StudentContentStatusInterface) {
    const path = `${personId}/studentContentStatuses`;

    const body = studentContentStatus;
    return this.post<StudentContentStatusInterface>(path, undefined, body);
  }

  public createTeacherTasks(personId: number, task: TaskInterface) {
    const path = `${personId}/teacherTasks`;

    const body = task;
    return this.post<TaskInterface>(path, undefined, body);
  }

  public createUnlockedFreePractices(personId: number, unlockedFreePractices: UnlockedFreePracticeInterface[]) {
    const path = `${personId}/createUnlockedFreePractices`;

    const params = this._getHttpParams({
      unlockedFreePractices,
    });

    return this.post<UnlockedFreePracticeInterface[]>(path, params);
  }

  public replaceUnlockedFreePracticesForBook(personId: number, productId: number, bookId: number, groupIds: number[]) {
    const path = `${personId}/replaceUnlockedFreePracticesForBook`;

    const body = { productId, bookId, groupIds };
    return this.post<UnlockedFreePracticeInterface[]>(path, undefined, body);
  }

  public deleteManyUnlockedFreePracticeRemote(personId: number, unlockedFreePracticeIds: number[]) {
    const path = `${personId}/deleteManyUnlockedFreePracticeRemote`;

    const params = this._getHttpParams({
      ufpIds: unlockedFreePracticeIds,
    });

    return this.delete<{ count: number }>(path, params);
  }

  public createUserLessons(personId: number, userLesson: UserLessonInterface) {
    const path = `${personId}/userLessons`;

    const body = userLesson;
    return this.post<UserLessonInterface>(path, undefined, body);
  }

  public getAccessTokens(personId: number): Observable<AccessTokenInterface[]> {
    const path = `${personId}/accessTokens`;
    const params = undefined;
    return this.get<AccessTokenInterface[]>(path, params);
  }

  public resetPassword(credentials: { email: string }): Observable<any> {
    const path = `reset`;
    return this.post(path, undefined, credentials).pipe(mapTo(true));
  }

  public checkMultipleUniques(personId: number, property: string, values: any[]): Observable<{ nonuniques: string[] }> {
    const path = `check-multiple-uniques`;
    const body = {
      key: personId,
      property: property,
      args: values,
    };
    return this.post<{ nonuniques: string[] }>(path, undefined, body);
  }

  public validatePasswordStrength(personId: number, password: string): Observable<any> {
    const path = `validate-password/${personId}`;
    const body = { password };
    return this.post(path, undefined, body).pipe(catchError((err) => throwError(err.error.error)));
  }

  public updateUser(personId: number, changedProps: Partial<PersonInterface>): Observable<PersonInterface> {
    return this.put(`updateUser/${personId}`, undefined, changedProps);
  }

  public createUser(user: Partial<PersonInterface>): Observable<PersonInterface> {
    return this.post(`createUser`, undefined, user);
  }

  public updatePassword(
    personId: number,
    { password, currentPassword }: { password: string; currentPassword?: string },
    accessToken?: string
  ): Observable<any> {
    const path = `${this.baseUrl}people/password/${personId}`;

    const headerFunction =
      accessToken && ((headers: HttpHeaders): HttpHeaders => headers.set('Authorization', accessToken));

    return this.httpClient.put(
      path,
      { password, currentPassword },
      { headers: headerFunction ? headerFunction(new HttpHeaders()) : undefined, withCredentials: true }
    );
  }

  public getRealms(personId: number): Observable<RealmInterface[]> {
    const path = `${personId}/realms`;
    return this.get<RealmInterface[]>(path);
  }

  public loginWithRealm(personId: number, realm: string): Observable<string> {
    const path = `${personId}/loginWithRealm/${realm}`;
    return this.get<string>(path);
  }

  public loginLink(personId: number): Observable<string> {
    const path = `person/${personId}/loginLink`;
    return this.get<string>(path);
  }

  public alerts(personId: number, lastUpdateTime?: number): Observable<AlertQueueInterface[]> {
    const path = `${personId}/alerts`;
    const params = lastUpdateTime ? { from: lastUpdateTime } : undefined;
    const httpParams = params ? this._getHttpParams(params) : undefined;

    return this.get<AlertQueueInterface[]>(path, httpParams);
  }

  public register(email: string, password: string, userType: string): Observable<any> {
    const path = 'register';
    return this.post(path, undefined, { email, password, type: userType });
  }

  public getCredentials(personId: number): Observable<PassportUserCredentialInterface[]> {
    const path = `${personId}/credentials`;
    const params = undefined;
    return this.get<PassportUserCredentialInterface[]>(path, params);
  }

  public saveResult(
    personId: number,
    resultId: number,
    score: number,
    time: number,
    status: string,
    cmi: string
  ): Observable<ResultInterface> {
    const path = `${personId}/result/${resultId}`;

    const body = {
      score,
      time,
      status,
      cmi,
    };

    return this.post<ResultInterface>(path, undefined, body);
  }

  public resultForUnlockedFreePractice(
    personId: number,
    unlockedFreePracticeId: number,
    eduContentId: number
  ): Observable<ResultInterface> {
    const path = `${personId}/unlockedFreePractice/${unlockedFreePracticeId}/result/${eduContentId}`;

    return this.get<ResultInterface>(path);
  }

  public createPersonPreferences(
    personId: number,
    personPreferences: PersonPreferenceInterface[]
  ): Observable<PersonPreferenceInterface[]> {
    const path = `${personId}/personPreferences`;
    return this.post<PersonPreferenceInterface[]>(path, undefined, personPreferences);
  }

  public getCurrent(): Observable<PersonInterface> {
    const path = `me`;

    return this.get<PersonInterface>(path).pipe(
      catchError(() => of(null)),
      tap((user) => {
        this.authApi.setUser(user?.id ? { id: user.id } : null);
      })
    );
  }

  public getCurrentId(): number {
    return this.authApi.getCurrentUserId();
  }

  public purchaseWithKwetons(price: number): Observable<{ kwetons: number }> {
    return this.post('me/spend-kwetons', undefined, { price });
  }

  public searchPeople(params: HttpParams): Observable<{ count: number; results: PersonInterface[] }> {
    return this.get('search', params);
  }

  public getPerson(id: number, params: HttpParams): Observable<PersonInterface> {
    return this.get(`${id}`, params);
  }

  public deletePerson(id: number) {
    const path = `deletePerson/${id}`;
    return this.delete<void>(path);
  }

  public switchRole(id: number, switchRoleTo: string) {
    return this.put(`${id}/switchRole`, undefined, {
      roleToSwitchTo: switchRoleTo,
    });
  }

  public mergeMembers(fromId: number, toId: number) {
    return this.put('merge-profiles', undefined, {
      fromPersonId: fromId,
      toPersonId: toId,
    });
  }

  public getTeacherWithStudents(teacherId: number): Observable<PersonInterface> {
    const path = `${teacherId}/students`;
    return this.get<PersonInterface>(path);
  }
}
